import React, { useEffect, useRef, useState } from 'react';
import Highcharts, { AlignValue, OptionsLayoutValue, VerticalAlignValue } from 'highcharts';
import HighchartsVariablePie from 'highcharts/modules/variable-pie';
import highcharts3d from 'highcharts/highcharts-3d';
import { FormControlLabel, Switch } from '@mui/material';
import { Methods } from '../../core/utility';

highcharts3d(Highcharts);
HighchartsVariablePie(Highcharts);

const methods = new Methods();

interface Props {
  dataSource: any[];
  title?: string;
  showInLegend?: boolean;
  height?: number;
  width?: number;
  verticalAlign?: VerticalAlignValue;
  align?: AlignValue;
  layout?: OptionsLayoutValue;
  property?: string;
  size?: string;
  className?: string;
  innerSize?: string;
  itemMarginBottom?: number;
  itemMarginTop?: number;
  spacingBottom?: number;
  marginBottom?: number;
  marginTop?: number;
  distance?: number;
  itemWidth?: number;
  responsiveItemWidth?: number;
  responsiveLegendWidth?: any;
  responsiveSize?: any;
  type?: any;
  zMin?: any;
  zMax?: any;
  options?: any;
  showToggle?: boolean;
}

export const PieChart: React.FC<Props> = (props: Props) => {
  const [showAsPercentage, setShowAsPercentage] = useState(true);
  const chartRef = useRef(null);

  const chartOptions: Highcharts.Options = {
    chart: {
      type: props.type || 'pie',
      backgroundColor: "transparent",
      height: props.height || 400,
      width: props.width,
      spacingBottom: props.spacingBottom ?? -10,
      marginBottom: props.marginBottom ?? 20,
      marginTop: props.marginTop ?? 0,
      marginLeft: 40,
      marginRight: 40,  
    },
    title: {
      text: props.title,
    },
    legend: {
      layout: props.layout || 'horizontal',
      align: props.align || 'center',
      verticalAlign: props.verticalAlign || 'bottom',
      itemMarginTop: props.itemMarginTop ?? 10,
      itemMarginBottom: props.itemMarginBottom ?? 10,
      itemWidth: props.itemWidth ?? 100,
      labelFormatter: function (this: any) {
        return `<span style="white-space: normal;">${this.name}: ${this.percentage?.toFixed(1) ?? 0} 123%</span>`;
      }
    },
    tooltip: {
      pointFormat: showAsPercentage
        ? "{series.name}: <b>{point.percentage:.1f}%</b>"
        : "{series.name}: <b>{point.y}</b>"
    },
    credits: {
      enabled: false
    },
    accessibility: {
      point: {
        valueSuffix: '%'
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        innerSize: props.innerSize || '70%',
        cursor: "pointer",
        borderRadius: 0,
        center: ['50%', '50%'],
        size: props.size || '100%',
        dataLabels: {
            enabled: true,
            distance: 10,
            crop: false,
            overflow: 'justify',
            filter: {
              property: 'percentage',
              operator: '>',
              value: 4
            },
            style: {
              fontSize: '11px',
              whiteSpace: 'normal',
              width: 40
            },
            formatter: function (this: any) {
              return showAsPercentage
                ? `${this.point.name}<br>${this.percentage.toFixed(1)}%`
                : `<b>${this.point.name}</b><br>${methods.convertExactNumber(this.y)}`;
            }
          },
        states: {
          inactive: {
            opacity: 1
          }
        }
      },
      variablepie: {
        minPointSize: 10,
        maxPointSize: 300,
        zMin: props.zMin || 0,
        zMax: props.zMax || 1000,
        dataLabels: {
          crop: false,
          style: {
            fontSize: '12px',
            textOverflow: 'none',
            whiteSpace: 'normal',
          },
          formatter: function (this: any) {
            return showAsPercentage
              ? `${this.point.name}<br>${this.percentage.toFixed(1)}%`
              : `<b>${this.point.name}</b><br>${methods.convertExactNumber(this.y)}`;
          }
        },
        states: {
          inactive: {
            opacity: 1
          }
        }
      }
    },
    series: [
      props.type === 'variablepie'
        ? {
            type: 'variablepie',
            name: '',
            data: props.dataSource,
            minPointSize: 10,
            innerSize: props.innerSize || '20%',
          }
        : {
            name: '',
            data: props.dataSource,
            showInLegend: props.showInLegend,
            type: props.type || 'pie',
          }
    ],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          plotOptions: {
            pie: {
              size: props.responsiveSize || '80%'
            }
          },
          legend: {
            width: props.responsiveLegendWidth,
            itemWidth: props.responsiveItemWidth
          }
        }
      }]
    },
    exporting: {
      enabled: false
    }
  };

  useEffect(() => {
    if (chartRef.current) {
      Highcharts.chart(chartRef.current, chartOptions);
    }
  }, [props.dataSource, showAsPercentage]);

  return (
    <>
      {props.showToggle && (
        <FormControlLabel
          className='toggle-percentage'
          control={
            <Switch
              size="small"
              checked={showAsPercentage}
              onChange={() => setShowAsPercentage(prev => !prev)}
            />
          }
          label="Show in Percentage"
        />
      )}
      <div ref={chartRef}  className={`pie-chart-container ${props.className || ''}`}></div>
    </>
  );
};
